import axios from "axios";
import { API_URL } from "../constants";
import { DIGITAL_HUMAN } from "@common/domain/auth";
import { checkSessionExpired } from "./Auth";
import { UserInfo } from "../contexts/UserInfoContext";
import { Persona } from "./Persona";
import { getTavusPersonaId } from "../helpers/personaDisplay";
import { getChatSession } from "./Chat";
import { getUserTimeZone } from "../utils/timezone";
import { isNotFoundError } from "./http-errors";
/*
  We'll add a 30-min expiry (exp) so rooms won't linger too long on your account.
  See other available options at https://docs.daily.co/reference#create-room
 */

/**
 * Sync any exchanges in `sourceChatId` that are missing from `targetChatId`.
 * Diff is by `exchangeId`, so this is safe to call in either direction
 * (chat → call before join, call → chat after leave) and idempotent if re-run.
 */
async function copyConversationHistory(
  userInfo: UserInfo,
  sourceChatId: string,
  targetChatId: string,
  personaId: string,
): Promise<boolean> {
  try {
    if (!sourceChatId || !targetChatId || sourceChatId === targetChatId) return true;

    if (checkSessionExpired(userInfo?.accessToken)) return false;
    const token = userInfo?.accessToken;

    console.log(`[HistoryCopy] Sync ${sourceChatId} -> ${targetChatId}`);

    const sourceSession = await getChatSession(userInfo, sourceChatId);
    const sourceExchanges = sourceSession?.conversation || [];
    if (sourceExchanges.length === 0) {
      console.log(`[HistoryCopy] Source ${sourceChatId} is empty/not found. Nothing to sync.`);
      return true;
    }

    let targetExchanges: any[] = [];
    try {
      const targetSession = await getChatSession(userInfo, targetChatId);
      targetExchanges = targetSession?.conversation || [];
    } catch (e: any) {
      if (!isNotFoundError(e)) {
        console.warn(`[HistoryCopy] Unexpected error fetching target ${targetChatId}:`, e.message);
      }
    }

    const targetIds = new Set(targetExchanges.map((e) => e.exchangeId));
    const missing = sourceExchanges.filter((e) => !targetIds.has(e.exchangeId));

    if (missing.length === 0) {
      console.log(`[HistoryCopy] Target ${targetChatId} already has all ${sourceExchanges.length} exchanges. Nothing to sync.`);
      return true;
    }

    console.log(`[HistoryCopy] Copying ${missing.length}/${sourceExchanges.length} missing exchanges to ${targetChatId}.`);

    let copiedAllExchanges = true;
    const copyExchange = async (exchange: any, index: number) => {
      console.log(`[HistoryCopy] Copying exchange ${exchange.exchangeId} (${index + 1}/${missing.length})`);
      const rawSources = Array.isArray(exchange.sources)
        ? exchange.sources
        : Object.values(exchange.sources || {});
      const sourcesArray = rawSources
        .map((source: any, index: number) => {
          const sourceNumber = source?.source_number ?? source?.sourceNumber ?? (index + 1);
          const name = source?.name || source?.title || `Source ${sourceNumber}`;
          const location = source?.location || '';
          if (!location) return null;
          return {
            name,
            location,
            score: source?.score,
            source_number: sourceNumber,
            chunk_content: source?.chunk_content ?? source?.chunkContent,
            page: source?.page,
          };
        })
        .filter(Boolean);

      const body = {
        question: exchange.question,
        result: exchange.response,
        sources: sourcesArray,
        metadata: exchange.metadata || '',
        personaId: personaId,
        exchangeId: exchange.exchangeId,
        isConversation: true,
      };

      try {
        await axios.post(
          `${API_URL}/chat/add/${encodeURIComponent(targetChatId)}?chatType=${DIGITAL_HUMAN}`,
          body,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch (addError: any) {
        copiedAllExchanges = false;
        console.error(
          `[HistoryCopy] Failed to add exchange ${exchange.exchangeId}:`,
          addError.response?.status,
          addError.response?.data || addError.message,
        );
        // Continue to next exchange despite failure
      }

      // Do not re-emit putResponseRating when copying dialogue history between sessions.
      // Call sessions (and standard chat sessions) are permanently persisted in DynamoDB
      // with their own ratings and indexed under the sparse feedbackIndex GSI. Re-submitting
      // the rating under targetChatId duplicates rows in the GSI for the same logical exchangeId,
      // which bloats index storage, doubles RCU consumption during feedback queries, and risks
      // desynchronization if a user subsequently modifies or clears feedback on one session.
    };

    const concurrency = 4;
    for (let index = 0; index < missing.length; index += concurrency) {
      await Promise.all(
        missing.slice(index, index + concurrency)
          .map((exchange, offset) => copyExchange(exchange, index + offset)),
      );
    }
    console.log(`[HistoryCopy] Finished sync of ${missing.length} exchange(s) to ${targetChatId}.`);
    return copiedAllExchanges;
  } catch (error) {
    console.error('[HistoryCopy] Error in copyConversationHistory:', error);
    return false;
  }
}

export const TAVUS_CUSTOM_GREETING_MAX_BYTES = 4_096;

function utf8ByteLength(character: string): number {
  const codePoint = character.codePointAt(0);
  if (codePoint === undefined) return 0;
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
}

export function truncateUtf8(value: string, maxBytes: number): string {
  let result = '';
  let usedBytes = 0;
  for (const character of value) {
    const characterBytes = utf8ByteLength(character);
    if (usedBytes + characterBytes > maxBytes) break;
    result += character;
    usedBytes += characterBytes;
  }
  return result;
}

export function buildTavusConversationBody(
  persona: Persona | undefined,
  conversationalContext?: Array<{ role: 'user' | 'assistant'; content: string }>,
  customGreeting?: string,
  callbackUrl?: string,
  hostname?: string,
) {
  const tavusBody: Record<string, string | undefined> = {
    replica_id: persona?.replicaId,
    persona_id: getTavusPersonaId(persona, hostname),
  };
  if (callbackUrl) tavusBody.callback_url = callbackUrl;
  if (conversationalContext && conversationalContext.length > 0) {
    tavusBody.conversational_context = JSON.stringify(conversationalContext);
  }
  if (customGreeting) {
    tavusBody.custom_greeting = truncateUtf8(customGreeting, TAVUS_CUSTOM_GREETING_MAX_BYTES);
  }
  return tavusBody;
}

async function startConversation(
  userInfo: UserInfo | undefined,
  persona: Persona | undefined,
  chatId?: string,
  conversationalContext?: Array<{ role: 'user' | 'assistant'; content: string }>,
  onConversationCreated?: (conversationId: string) => void,
  onConversationStarted?: (conversationId: string) => void,
  customGreeting?: string,
  chapterSlug?: string,
) {
  console.log('startConversation called with chatId:', chatId);
  if (!checkSessionExpired(userInfo?.accessToken)) {
    const token = userInfo?.accessToken;
    try {
      const callbackUrl = process.env.REACT_APP_TAVUS_WEBHOOK_URL;
      const tavusBody: any = buildTavusConversationBody(
        persona,
        conversationalContext,
        customGreeting,
        callbackUrl,
      );

      const response = await fetch(
        `${process.env.REACT_APP_TAVUS_BASE_URL}${process.env.REACT_APP_TAVUS_CONVERSATIONS_ENDPOINT}`,
        {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_TAVUS_X_API_KEY || "",
          },
          body: JSON.stringify(tavusBody),
        })
    
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Tavus API error:', errorData);
        throw new Error(errorData.message || `Tavus API error: ${response.status}`);
      }

      let conversation = await response.json();
      console.log('Tavus conversation response:', conversation)
      
      if (!conversation.conversation_id || !conversation.conversation_url) {
        throw new Error('Tavus did not return a complete conversation.');
      }

      const headers = {
        Authorization: `Bearer ${token}`,
      }
      const requestBody: any = {
        personaId: persona?.id,
        timeZone: typeof getUserTimeZone === 'function' ? getUserTimeZone() : 'America/Chicago',
      }
      if (chatId) {
        requestBody.chatId = chatId;
      }
      if (chapterSlug) {
        requestBody.chapterSlug = chapterSlug;
      }

      await axios.post(
        `${API_URL}/conversation/${conversation.conversation_id}/start`,
        requestBody,
        {
          headers: headers,
        });

      // Do not surface a joinable Tavus room until its server-side mapping is
      // durable. Tavus can invoke the custom LLM immediately on a greeting.
      try {
        onConversationCreated?.(conversation.conversation_id);
      } catch (observerError) {
        console.warn('[Call] conversation-created observer failed', observerError);
      }

      if (chatId) {
        console.log(`Triggering history copy from ${chatId} to ${conversation.conversation_id}`);
        // Copy history from previous chatId to the new Tavus conversation_id
        const historyCopied = await copyConversationHistory(
          userInfo as UserInfo,
          chatId,
          conversation.conversation_id,
          persona?.id || "",
        );
        if (!historyCopied) {
          throw new Error('Conversation history could not be copied into the video call.');
        }
      } else {
        console.log('No previous chatId provided, skipping history copy.');
      }

      try {
        onConversationStarted?.(conversation.conversation_id);
      } catch (observerError) {
        console.warn('[Call] conversation-started observer failed', observerError);
      }

      return conversation;
    } catch (error: any) {
      console.error('startConversation error:', error);
      throw error;
    }
  } else return undefined;
}

async function endConversation(userInfo: UserInfo | undefined, conversationId: string | null): Promise<boolean> {
  try {
    const token = userInfo?.accessToken;
    const end_tavus_conversation = fetch(
      `${process.env.REACT_APP_TAVUS_BASE_URL}${process.env.REACT_APP_TAVUS_CONVERSATIONS_ENDPOINT}/${conversationId}/end`,
      {
          method: 'POST',
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_TAVUS_X_API_KEY || ""
          }
      })
  
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const end_conversation = axios.put(
      `${API_URL}/conversation/${conversationId}/end`,
      {},
      {
        headers: headers
      });
  
    const [tavusResponse] = await Promise.all([end_tavus_conversation, end_conversation])
    if (!tavusResponse.ok) return false;

    return true
  } catch (error) {
    return false
  }
}

const CallApi = { startConversation, endConversation, copyConversationHistory };
export { copyConversationHistory };
export default CallApi;
